import {
  type FetchQueryOptions,
  QueryClient,
  dehydrate,
} from "@tanstack/react-query";

/**
 * Fresh QueryClient per server request; on the client `_app` keeps one in state.
 * `staleTime > 0` stops the client immediately refetching data the server just
 * prefetched and hydrated.
 */
export const makeQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  });

/**
 * Fetch a query on the server and return both the response and the dehydrated cache.
 * The caller needs the data to decide 404 / SEO tags, and the cache to hydrate the
 * client without a second request — pass it as the `dehydratedState` page prop, which
 * the app's `HydrationBoundary` (wired in `_app`) picks up.
 *
 *   const { data, dehydratedState } = await fetchAndDehydrate(
 *     searchRouteQueryOptions(route)
 *   );
 *
 * Rejects if the query fails. `retry: false` because a 404 from the service is an
 * answer, not a blip — backing off three times would just delay the response.
 *
 * `forHydration` slims what goes into the page HTML without touching what the
 * caller gets back: the returned `data` is the full response, the dehydrated
 * cache entry is `forHydration(data)`. The cache key and every other part of
 * the entry stay the same, so the client still hydrates it instead of
 * refetching.
 */
export const fetchAndDehydrate = async <TData>(
  // `any` generics so the typed objects from `queryOptions(...)` are accepted — a
  // narrower queryKey type triggers queryFn contravariance errors. The options are
  // still validated by fetchQuery at the call.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: FetchQueryOptions<TData, any, TData, any>,
  forHydration?: (data: TData) => TData
): Promise<{ data: TData; dehydratedState: unknown }> => {
  const queryClient = makeQueryClient();
  const data = await queryClient.fetchQuery({ ...options, retry: false });

  if (forHydration) {
    queryClient.setQueryData(options.queryKey, forHydration(data));
  }

  return { data, dehydratedState: dehydrate(queryClient) };
};
